import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL!;
const SUPABASE_ANON = process.env.REACT_APP_SUPABASE_ANON_KEY!;

if (!SUPABASE_URL || !SUPABASE_ANON) {
  throw new Error('Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_ANON_KEY in frontend/.env');
}

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

// ── Authenticated fetch wrapper ──────────────────────────────────────────────
//
// Drop-in replacement for `fetch()` that attaches the current Supabase JWT as
// an `Authorization: Bearer <token>` header. When no session exists (anonymous
// demo visitor), the call goes out without an Authorization header — the
// backend's `get_optional_user` + `require_project_access` dependencies handle
// the demo carve-out.
//
// `supabase.auth.getSession()` returns the cached session and silently refreshes
// the token in the background when it's near expiry, so individual call sites
// never need to think about token lifetime.
//
// Usage:
//   const resp = await authedFetch(`${API_BASE}/api/projects`);
//   const resp = await authedFetch(url, { method: 'POST', body: JSON.stringify({...}) });
//
// FormData uploads work too — pass a FormData body and DON'T set Content-Type
// (the browser sets the multipart boundary automatically):
//   const fd = new FormData();
//   fd.append('file', file);
//   const resp = await authedFetch(url, { method: 'POST', body: fd });

export async function authedFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  // Pull the current session. getSession() returns the cached token and
  // triggers a background refresh if expiry is close.
  const { data: { session } } = await supabase.auth.getSession();

  // Build headers without clobbering anything the caller passed in.
  const headers = new Headers(init.headers);

  if (session?.access_token) {
    headers.set('Authorization', `Bearer ${session.access_token}`);
  }

  // Default to JSON content-type when the caller is sending a body and hasn't
  // already specified one — but skip this for FormData (multipart uploads),
  // where the browser must set Content-Type itself to include the boundary.
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData;
  if (init.body && !isFormData && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  return fetch(input, { ...init, headers });
}